import { IonicVue } from '@ionic/vue';
import * as Sentry from "@sentry/vue";
import { createApp } from 'vue';
import { VueReCaptcha } from 'vue-recaptcha-v3';
import Toast from 'vue-toastification';
import 'vue-toastification/dist/index.css';
import App from './App.vue';
import './assets/styles/main.scss';
import './assets/tailwind.css';
import { setupAuthInterceptor } from './interceptors/authInterceptor';
import { setupI18n } from './plugins/i18n';
import router from './router';
import store from './store';
import { trackInAppBrowserSuccess, trackWebKitErrorsPrevented } from './utils/analytics';
import { setI18n } from './utils/i18nAccessor';


// Detect in-app browsers that aggressively block third-party scripts
const ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
const isInAppBrowser = /FBAN|FBAV|FB_IAB|FB4A|FBIOS/i.test(ua) || /Instagram/i.test(ua);

// Create the Vue app with custom configuration
const app = createApp(App)
	.use(store)
	.use(router)
	.use(IonicVue);

// Initialize i18n before mounting (async since we lazy-load messages)
(async () => {
	const i18n = await setupI18n();
	app.use(i18n);
	setI18n(i18n);

// Configure Vue app options - increase async component timeout for in-app browsers
app.config.compilerOptions = app.config.compilerOptions || {};
app.config.performance = true;
// Set a higher timeout for async components, especially in in-app browsers
app.config.globalProperties.__VUE_PROD_DEVTOOLS__ = false;
// Increase the timeout for async components (default is 5000ms)
app.config.asyncComponentTimeout = isInAppBrowser ? 15000 : 10000;

// Log the increased timeout
if (isInAppBrowser) {
	console.log('📱 In-app browser detected - increasing async component timeout to 15000ms');
}

// CRITICAL: Protect paid Facebook ad traffic from webkit errors
// These users are costing money - we cannot let them encounter JS errors that break the app
if (isInAppBrowser && typeof window !== 'undefined') {
	console.log('🚨 Facebook/Instagram in-app browser detected - protecting paid traffic');

	// Track how many webkit errors we're preventing (for ROI analysis)
	let webkitErrorCount = 0;
	const maxErrorsToLog = 5; // Prevent log spam

	window.addEventListener('error', function (event) {
		if (event.error && event.error.message &&
			(event.error.message.includes('webkit') ||
				event.error.message.includes('messageHandlers') ||
				event.error.message.includes('postMessage'))) {

			webkitErrorCount++;

			if (webkitErrorCount <= maxErrorsToLog) {
				// Log for business analysis - these are paid users!
				console.error('💰 PAID TRAFFIC WEBKIT ERROR PREVENTED:', {
					error: event.error.message,
					filename: event.filename,
					lineno: event.lineno,
					userAgent: navigator.userAgent,
					url: window.location.href,
					errorCount: webkitErrorCount,
					timestamp: new Date().toISOString()
				});
			}

			// AGGRESSIVELY prevent error propagation - this is paid traffic!
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			return false;
		}
	}, true);

	window.addEventListener('unhandledrejection', function (event) {
		if (event.reason && event.reason.message &&
			(event.reason.message.includes('webkit') ||
				event.reason.message.includes('messageHandlers'))) {

			webkitErrorCount++;

			if (webkitErrorCount <= maxErrorsToLog) {
				console.error('💰 PAID TRAFFIC WEBKIT PROMISE REJECTION PREVENTED:', {
					reason: event.reason.message,
					userAgent: navigator.userAgent,
					url: window.location.href,
					errorCount: webkitErrorCount,
					timestamp: new Date().toISOString()
				});
			}

			// Prevent promise rejection from being unhandled
			event.preventDefault();
			return false;
		}
	}, true);

	// Send business intelligence data about webkit issues affecting paid traffic
	setTimeout(() => {
		if (webkitErrorCount > 0) {
			trackWebKitErrorsPrevented(webkitErrorCount, navigator.userAgent);
		}
		// Track successful initialization in problematic browser
		trackInAppBrowserSuccess(isInAppBrowser ? 'facebook_inapp' : 'unknown');
	}, 5000);
}

Sentry.init({
	app,
	dsn: "https://e3bf67ee4d32311608af06a5f0cabd52@o4508552829927424.ingest.de.sentry.io/4510051734913104",
	// Setting this option to true will send default PII data to Sentry.
	sendDefaultPii: true,
	integrations: [
		Sentry.replayIntegration()
	],
	// Session Replay
	replaysSessionSampleRate: 0.1,
	replaysOnErrorSampleRate: 1.0,
	denyUrls: [
		/googletagmanager\.com/i,
		/hotjar\.com/i,
		/connect\.facebook\.net/i,
		/simpleanalyticscdn\.com/i,
	],
	beforeSend(event) {
		// Filter out async component timeout errors in Facebook browsers
		if (event.exception && event.exception.values) {
			for (const exception of event.exception.values) {
				// Check for async component timeout errors
				if (exception.value &&
					exception.value.includes('Async component timed out after') &&
					isInAppBrowser) {

					// Log for business analysis but don't send to Sentry
					console.error('💰 Facebook Async component timeout (filtered from Sentry):', {
						error: exception.value,
						browser: event.tags?.browser,
						url: event.request?.url,
						userId: event.user?.id,
						timestamp: new Date().toISOString()
					});

					// Track this event for business analytics
					if (typeof window.gtag === 'function') {
						window.gtag('event', 'async_component_timeout', {
							event_category: 'performance_issues',
							event_label: 'facebook_async_timeout',
							custom_parameters: {
								browser: event.tags?.browser,
								error_type: 'async_component_timeout'
							}
						});
					}

					return null; // Don't send to Sentry
				}

				// BUSINESS CRITICAL: Only filter Facebook webkit errors that don't affect our core functionality
				if (exception.value &&
					exception.value.includes('webkit.messageHandlers') &&
					// Only filter if it's specifically from Facebook's translation scripts
					(event.tags?.browser?.includes('Facebook') ||
						event.request?.headers?.['User-Agent']?.includes('FBAN'))) {

					// Log for business analysis - this affects paid traffic ROI
					console.error('💰 Facebook WebKit error affecting paid ads (filtered from Sentry):', {
						error: exception.value,
						browser: event.tags?.browser,
						url: event.request?.url,
						userId: event.user?.id,
						timestamp: new Date().toISOString()
					});

					// Send to analytics for business intelligence
					if (typeof window.gtag === 'function') {
						window.gtag('event', 'webkit_error_filtered', {
							event_category: 'paid_traffic_issues',
							event_label: 'facebook_webkit_messagehandlers',
							custom_parameters: {
								browser: event.tags?.browser,
								error_type: 'webkit_messagehandlers'
							}
						});
					}

					return null; // Don't send to Sentry to reduce noise
				}
			}
		}

		return event;
	}
});

app.use(Toast, {
	position: 'top-center',
});
// Always initialize reCAPTCHA; in in-app browsers prefer recaptcha.net domain
app.use(VueReCaptcha, {
	siteKey: '6LeHOakqAAAAADwMgc_SwforxRI2RgqfakhaRGyt',
	loaderOptions: {
		autoHideBadge: true,
		size: 'invisible',
		badge: 'bottomleft',
		// Use recaptcha.net in in-app browsers to avoid blocked google domains
		useRecaptchaNet: !!isInAppBrowser,
	},
});

// Setup auth interceptor before mounting the app
setupAuthInterceptor();

app.mount('#app');

// Hide loading screen when Vue app is ready
if (typeof window.hideLoadingScreen === 'function') {
	// Delay to ensure the app is fully rendered and positioned
	setTimeout(() => {
		window.hideLoadingScreen();
	}, 800);
}

// Log application startup
console.log('Dofantasy AI client application started with auth interceptor');

})();
